import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { IPagination } from '../Interfaces/IPagination';
import { buildLegalClientSearchUri } from '../helper/legalClientSearch';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';

interface Props {
  /** Aktuell ausgewählte Schuldner (aufgelöste Objekte, damit die Chips immer
   *  ihr Label finden — auch wenn die Dropdown-Optionen gefiltert sind). */
  value: ILegalClient[];
  /** Auswählbare Mandanten im lokalen Modus (serverSearch=false). */
  options?: ILegalClient[];
  onChange: (newValue: ILegalClient[]) => void;
  label?: string;
  size?: 'small' | 'medium';
  /**
   * Server-seitige Suche über ALLE Mandanten (#1197). Statt der `options`-Liste
   * wird ab 2 Zeichen live über den paginierten `/legalclient`-Endpoint gesucht
   * — kein Rendern tausender Optionen, kein Main-Thread-Freeze.
   */
  serverSearch?: boolean;
}

const clientLabel = (client: ILegalClient) =>
  `${client.FirstName ?? ''} ${client.LastName ?? ''}`.trim();

/**
 * Tippbares Mehrfach-Auswahlfeld für Zahlungsschuldner (#1182).
 * Ersetzt das frühere <Select multiple> in InvoiceRow und InvoiceEdit.
 * Optional mit server-seitiger Suche über alle Mandanten (#1197).
 */
export const DebtorAutocomplete = ({
  value,
  options = [],
  onChange,
  label = 'Schuldner',
  size = 'small',
  serverSearch = false,
}: Props) => {
  // Server-Suche: Eingabe entprellt (350 ms), Hook lädt erst ab 2 Zeichen.
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(searchInput), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const searchUri = serverSearch
    ? buildLegalClientSearchUri(debounced)
    : undefined;
  const { legalClients, isLoadingLegalClients } = useGetLegalClients(searchUri);

  const hits: ILegalClient[] = serverSearch
    ? ((legalClients as IPagination<ILegalClient[]> | undefined)?.results ?? [])
    : options;

  // Jeder ausgewählte Schuldner MUSS auch als Option vorhanden sein, sonst warnt
  // MUI ("none of the options match with value") und der Chip verliert sein
  // Label. Relevant im gefilterten Modus (InvoiceEdit-Schalter) und in der
  // Server-Suche (Treffer wechseln bei jeder Eingabe).
  const mergedOptions = Array.from(
    new Map([...hits, ...value].map(c => [c.idLegalClient, c])).values()
  );

  return (
    <Autocomplete<ILegalClient, true, false, false>
      multiple
      size={size}
      fullWidth
      disableCloseOnSelect
      options={mergedOptions}
      value={value}
      isOptionEqualToValue={(o, v) => o.idLegalClient === v.idLegalClient}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={clientLabel}
      // Server-Suche: Filterung passiert serverseitig -> lokalen Filter aus.
      filterOptions={serverSearch ? x => x : undefined}
      loading={serverSearch ? isLoadingLegalClients : undefined}
      inputValue={serverSearch ? searchInput : undefined}
      onInputChange={
        serverSearch
          ? (_, newInput, reason) => {
              // 'reset' feuert bei Auswahl/Blur — Eingabe dann nicht leeren.
              if (reason === 'reset') return;
              setSearchInput(newInput);
            }
          : undefined
      }
      noOptionsText={
        serverSearch
          ? searchInput.trim().length < 2
            ? 'Mindestens 2 Zeichen eingeben …'
            : 'Keine Treffer'
          : undefined
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.idLegalClient}>
          <Checkbox checked={selected} sx={{ mr: 1 }} />
          {clientLabel(option)}
          {option.Email ? (
            <Typography
              component='span'
              variant='body2'
              color='text.secondary'
              sx={{ ml: 1 }}
            >
              · {option.Email}
            </Typography>
          ) : null}
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder='Schuldner suchen…'
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {serverSearch && isLoadingLegalClients ? (
                  <CircularProgress color='inherit' size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
